import { useState, useEffect, useCallback, useMemo } from 'react'
import { toast } from 'sonner'
import i18n from '@/i18n'

import type { ShareSettingsAdapter, RawCollaborator, RawGuest, RawOrgMember, SharingMode } from './ShareSettingsTypes'
import type { CollaboratorEntry } from './CollaboratorList'
import type { GuestEntry } from './GuestList'
import type { OrgMemberOption } from './MemberSearchInput'

export type { SharingMode } from './ShareSettingsTypes'

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

function mapCollaborators(raw: Array<RawCollaborator>): Array<CollaboratorEntry> {
  return raw.map((c) => ({
    id: c.id,
    userId: c.userId,
    name: c.user?.name || c.user?.email || i18n.t('unknown', { ns: 'common' }),
    initials: getInitials(c.user?.name || c.user?.email || '??'),
    role: c.role as 'owner' | 'collaborator',
    avatarSeed: c.user?.avatarSeed,
  }))
}

function mapGuests(raw: Array<RawGuest>): Array<GuestEntry> {
  return raw.map((g) => ({ id: g.id, email: g.email, expiresAt: g.expiresAt }))
}

function mapOrgMembers(raw: Array<RawOrgMember>): Array<OrgMemberOption> {
  return raw.map((m) => ({
    userId: m.id,
    name: m.name || m.email || i18n.t('unknown', { ns: 'common' }),
    email: m.email || '',
    initials: getInitials(m.name || m.email || '??'),
    avatarSeed: m.avatarSeed,
  }))
}

interface UseShareSettingsOptions {
  adapter: ShareSettingsAdapter
  open: boolean
  onOpen?: () => void
}

export function useShareSettings(options: UseShareSettingsOptions) {
  const { adapter, open, onOpen } = options

  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)

  const [sharingMode, setSharingMode] = useState<SharingMode>('private')
  const [collaborators, setCollaborators] = useState<Array<CollaboratorEntry>>([])
  const [guests, setGuests] = useState<Array<GuestEntry>>([])
  const [orgMembers, setOrgMembers] = useState<Array<OrgMemberOption>>([])
  const [shareUrl, setShareUrl] = useState<string | null>(null)
  const [isPublic, setIsPublic] = useState(false)

  const [guestChatEnabled, setGuestChatEnabled] = useState(false)
  const [guestChatCap, setGuestChatCap] = useState(10)
  const supportsGuestChat = !!adapter.setGuestChat

  const [saving, setSaving] = useState(false)

  const loadData = useCallback(async () => {
    setLoading(true)
    setLoadError(false)
    try {
      if (adapter.ensureShareToken) {
        try {
          await adapter.ensureShareToken()
        } catch { /* token creation is best-effort */ }
      }

      const [sharingData, shareStatus, membersRaw] = await Promise.all([
        adapter.loadSharingData(),
        adapter.loadShareStatus(),
        adapter.loadOrgMembers(),
      ])

      const mode = (sharingData.sharingMode || 'private') as SharingMode
      setSharingMode(mode)
      setCollaborators(mapCollaborators(sharingData.collaborators || []))
      setGuests(mapGuests(sharingData.guests || []))
      setOrgMembers(mapOrgMembers(membersRaw))
      setShareUrl(shareStatus.shareUrl || null)
      setIsPublic(shareStatus.isPublic || false)

      if (adapter.loadGuestChatSettings) {
        try {
          const gc = await adapter.loadGuestChatSettings()
          setGuestChatEnabled(gc.guestChatEnabled)
          setGuestChatCap(gc.guestChatCap)
        } catch { /* guest chat not available */ }
      }
    } catch {
      setLoadError(true)
    } finally {
      setLoading(false)
    }
  }, [adapter])

  useEffect(() => {
    if (open) {
      onOpen?.()
      loadData()
    }
  }, [open, loadData, onOpen])

  const excludedUserIds = useMemo(() => {
    return collaborators.map((c) => c.userId)
  }, [collaborators])

  async function handleSelectMode(mode: SharingMode) {
    if (mode === sharingMode) return
    setSaving(true)
    try {
      await adapter.setSharingMode(mode)
      setSharingMode(mode)
      toast.success(i18n.t('sharing.updated', { ns: 'app' }))
    } catch {
      toast.error(i18n.t('sharing.updateModeFailed', { ns: 'app' }))
    } finally {
      setSaving(false)
    }
  }

  async function handleAddCollaborator(member: OrgMemberOption) {
    const optimistic: CollaboratorEntry = {
      id: `pending-${member.userId}`,
      userId: member.userId,
      name: member.name,
      initials: member.initials,
      role: 'collaborator',
      avatarSeed: member.avatarSeed,
    }
    setCollaborators((prev) => [...prev, optimistic])
    try {
      await adapter.addCollaborator(member.userId)
      await loadData()
    } catch {
      setCollaborators((prev) => prev.filter((c) => c.id !== optimistic.id))
      toast.error(i18n.t('sharing.addFailed', { ns: 'app', name: member.name }))
    }
  }

  async function handleRemoveCollaborator(id: string) {
    const removed = collaborators.find((c) => c.id === id)
    setCollaborators((prev) => prev.filter((c) => c.id !== id))
    try {
      await adapter.removeCollaborator(id)
    } catch {
      if (removed) setCollaborators((prev) => [...prev, removed])
      toast.error(i18n.t('sharing.removeCollaboratorFailed', { ns: 'app' }))
    }
  }

  async function handleAddGuest(email: string) {
    if (guests.some((g) => g.email === email)) return
    const optimistic: GuestEntry = {
      id: `pending-${email}`,
      email,
      expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
    }
    setGuests((prev) => [...prev, optimistic])
    try {
      const result = await adapter.addGuest(email)
      if (result && !result.invitationEmailSent) {
        toast.warning(i18n.t('sharing.inviteEmailNotSent', { ns: 'app', email }))
      }
      await loadData()
    } catch {
      setGuests((prev) => prev.filter((g) => g.id !== optimistic.id))
      toast.error(i18n.t('sharing.inviteFailed', { ns: 'app', email }))
    }
  }

  async function handleRemoveGuest(id: string) {
    const removed = guests.find((g) => g.id === id)
    setGuests((prev) => prev.filter((g) => g.id !== id))
    try {
      await adapter.removeGuest(id)
    } catch {
      if (removed) setGuests((prev) => [...prev, removed])
      toast.error(i18n.t('sharing.removeGuestFailed', { ns: 'app' }))
    }
  }

  async function handleGuestChatToggle() {
    if (!adapter.setGuestChat) return
    const next = !guestChatEnabled
    setGuestChatEnabled(next)
    try {
      await adapter.setGuestChat(next, guestChatCap)
    } catch {
      setGuestChatEnabled(!next)
      toast.error(i18n.t('sharing.guestChatFailed', { ns: 'app' }))
    }
  }

  async function handleGuestChatCapChange(val: number) {
    if (!adapter.setGuestChat) return
    setGuestChatCap(val)
    try {
      await adapter.setGuestChat(guestChatEnabled, val)
    } catch {
      toast.error(i18n.t('sharing.messageCapFailed', { ns: 'app' }))
    }
  }

  async function handleRegenerateLink() {
    if (!adapter.regenerateLink) return
    setSaving(true)
    try {
      const result = await adapter.regenerateLink()
      setShareUrl(result.shareUrl)
      toast.success(i18n.t('sharing.linkRegenerated', { ns: 'app' }))
    } catch {
      toast.error(i18n.t('sharing.regenerateFailed', { ns: 'app' }))
    } finally {
      setSaving(false)
    }
  }

  const isRestricted = isPublic && sharingMode === 'organization'

  async function handleSetAccessLevel(restricted: boolean) {
    const mode: SharingMode = restricted ? 'organization' : 'shared'
    setSaving(true)
    try {
      await adapter.setSharingMode(mode)
      setSharingMode(mode)
      toast.success(restricted
        ? i18n.t('sharing.accessRestricted', { ns: 'app' })
        : i18n.t('sharing.anyoneWithLink', { ns: 'app' }))
    } catch {
      toast.error(i18n.t('sharing.accessLevelFailed', { ns: 'app' }))
    } finally {
      setSaving(false)
    }
  }

  const linkSharingEnabled = !!shareUrl

  async function handleToggleLinkSharing(enabled: boolean) {
    setSaving(true)
    try {
      if (enabled) {
        const result = await adapter.enableSharing()
        setShareUrl(result.shareUrl)
        setIsPublic(true)
        toast.success(i18n.t('sharing.linkEnabled', { ns: 'app' }))
      } else {
        await adapter.disableSharing()
        setShareUrl(null)
        setIsPublic(false)
        toast.success(i18n.t('sharing.linkDisabled', { ns: 'app' }))
      }
    } catch {
      toast.error(i18n.t('sharing.linkToggleFailed', { ns: 'app' }))
    } finally {
      setSaving(false)
    }
  }

  const canRegenerate = !!adapter.regenerateLink

  return {
    loading,
    loadError,
    loadData,
    sharingMode,
    collaborators,
    guests,
    orgMembers,
    shareUrl,
    saving,
    guestChatEnabled,
    guestChatCap,
    supportsGuestChat,
    excludedUserIds,
    handleSelectMode,
    handleAddCollaborator,
    handleRemoveCollaborator,
    handleAddGuest,
    handleRemoveGuest,
    handleGuestChatToggle,
    handleGuestChatCapChange,
    handleRegenerateLink,
    canRegenerate,
    isPublic,
    linkSharingEnabled,
    handleToggleLinkSharing,
    isRestricted,
    handleSetAccessLevel,
  }
}
